import EdgeScroll from '../components/EdgeScroll';
import React, { useState, useEffect } from 'react';
import {
  Box, Title, Paper, Text, Group, Stack, Select, Slider, SimpleGrid,
  Table, Badge, Flex, Loader, ThemeIcon,
} from '@mantine/core';
import { CurrencyDollar, TrendUp, CursorClick, Target, Lightning, ChartBar, Lock } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import api from '../utils/api';
import StickyHeader from '../components/StickyHeader';
import { currencySymbol } from '../utils/money';

const fmt = (v, decimals = 0) => Number(v || 0).toLocaleString('pl-PL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

function MetricCard({ label, value, unit, icon, color, delta, isCurrent }) {
  const Icon = icon;
  return (
    <Paper withBorder p="sm" radius="md" style={isCurrent ? { borderColor: 'var(--mantine-color-brand-3)', backgroundColor: 'var(--mantine-color-brand-0)' } : undefined}>
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed">{label}</Text>
        <ThemeIcon variant="light" color={color || 'brand'} size="sm" radius="sm"><Icon size={14} /></ThemeIcon>
      </Group>
      <Group gap={4} align="baseline">
        <Text size="lg" fw={700} ff="monospace">{value}</Text>
        {unit && <Text size="xs" c="dimmed">{unit}</Text>}
      </Group>
      {delta !== undefined && delta !== 0 && (
        <Text size="xs" c={delta > 0 ? 'green.6' : 'red.6'} fw={500} mt={2}>
          {delta > 0 ? '+' : ''}{delta}%
        </Text>
      )}
    </Paper>
  );
}

export default function BudgetPlanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [projections, setProjections] = useState(null);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [sliderValue, setSliderValue] = useState(100);

  const enabled = user?.organization?.budget_planner_enabled;

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    api.get('/ads/budget-planner/campaigns/')
      .then(res => setCampaigns(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const runProjection = async (campaignId) => {
    setPlannerLoading(true);
    try {
      const res = await api.post('/ads/budget-planner/', { campaign_id: campaignId });
      setCampaignInfo(res.data.campaign);
      setProjections(res.data.projections);
      setSliderValue(100);
    } catch {
      setProjections(null);
    } finally {
      setPlannerLoading(false);
    }
  };

  const handleCampaignChange = (id) => {
    setSelectedCampaign(id);
    if (id) runProjection(id);
    else { setProjections(null); setCampaignInfo(null); }
  };

  // Find the projection closest to the slider value
  const currentProjection = projections?.find(p => p.is_current);
  const selectedProjection = projections?.reduce((best, p) => {
    const target = sliderValue / 100;
    return Math.abs(p.multiplier - target) < Math.abs(best.multiplier - target) ? p : best;
  }, projections?.[0]);

  // Symbol waluty konta wybranej kampanii (koszty/budżety są RAW w tej walucie).
  const selCampaignObj = campaigns.find(c => String(c.id) === String(selectedCampaign));
  const curSym = currencySymbol(campaignInfo?.currency || selCampaignObj?.currency || 'PLN');

  const calcDelta = (projected, current) => {
    if (!current || current === 0) return 0;
    return Math.round(((projected - current) / current) * 100);
  };

  if (!enabled) return (
    <Box px="lg" py="md">
      <SEO title={t('budgetPlanner.seoTitle')} path="/app/planner" noIndex />
      <Title order={3} mb="md">{t('budgetPlanner.title')}</Title>
      <Paper withBorder p="xl" radius="md" ta="center">
        <ThemeIcon variant="light" color="gray" size="xl" radius="md" mx="auto" mb="sm">
          <Lock size={24} />
        </ThemeIcon>
        <Text size="sm" fw={500} mb={4}>{t('budgetPlanner.disabled.title')}</Text>
        <Text size="xs" c="dimmed" maw={400} mx="auto">
          {t('budgetPlanner.disabled.text')}
        </Text>
      </Paper>
    </Box>
  );

  if (loading) return (
    <Flex align="center" justify="center" h={300}>
      <AnimatedLogo size={48} />
    </Flex>
  );

  const campaignOptions = campaigns.map(c => ({
    value: c.id,
    label: t('budgetPlanner.campaignOption', { name: c.name, budget: fmt(c.budget_amount, 2), currency: currencySymbol(c.currency) }),
  }));

  const sliderMarks = [
    { value: 50, label: '50%' },
    { value: 75, label: '75%' },
    { value: 100, label: '100%' },
    { value: 125, label: '125%' },
    { value: 150, label: '150%' },
    { value: 200, label: '200%' },
  ];

  return (
    <Box px="lg" py="md">
      <SEO title={t('budgetPlanner.seoTitle')} path="/app/planner" noIndex />
      <Group justify="space-between" mb="md">
        <Title order={3}>{t('budgetPlanner.title')}</Title>
      </Group>

      <Paper withBorder p="md" radius="md" mb="md">
        <Text size="sm" fw={500} mb="sm">{t('budgetPlanner.selectHeading')}</Text>
        <Select
          placeholder={t('budgetPlanner.selectPlaceholder')}
          data={campaignOptions}
          value={selectedCampaign}
          onChange={handleCampaignChange}
          size="sm"
          clearable
          styles={{ input: { cursor: 'pointer' } }}
        />
      </Paper>

      {plannerLoading && (
        <Flex align="center" justify="center" h={200}>
          <AnimatedLogo size={48} />
        </Flex>
      )}

      {!plannerLoading && projections && campaignInfo && selectedProjection && (
        <Stack gap="md">
          {/* Budget slider */}
          <Paper withBorder p="lg" radius="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>{t('budgetPlanner.dailyBudget')}</Text>
              <Group gap={4} align="baseline">
                <Text size="lg" fw={700} ff="monospace" c="brand">
                  {fmt(selectedProjection.budget_daily, 2)} {curSym}
                </Text>
                <Text size="xs" c="dimmed">{t('budgetPlanner.perDaySlash')}</Text>
              </Group>
            </Group>
            <Text size="xs" c="dimmed" mb="md">
              {t('budgetPlanner.currentBudget', { daily: fmt(campaignInfo.current_budget, 2), monthly: fmt(campaignInfo.current_budget * 30, 2), currency: curSym })}
            </Text>
            <Slider
              value={sliderValue}
              onChange={setSliderValue}
              min={50}
              max={200}
              step={5}
              marks={sliderMarks}
              color="brand"
              size="lg"
              styles={{
                markLabel: { fontSize: 11, marginTop: 6 },
                thumb: { cursor: 'pointer' },
              }}
            />
            <Text size="xs" c="dimmed" ta="center" mt="lg">
              {t('budgetPlanner.perMonth', { value: fmt(selectedProjection.budget_monthly, 2), currency: curSym })}
            </Text>
          </Paper>

          {/* Projected metrics */}
          <SimpleGrid cols={{ base: 2, md: 3, lg: 6 }} spacing="sm">
            <MetricCard
              label={t('budgetPlanner.metrics.clicks')}
              value={fmt(selectedProjection.clicks)}
              icon={CursorClick}
              color="blue"
              delta={calcDelta(selectedProjection.clicks, currentProjection?.clicks)}
              isCurrent={selectedProjection.is_current}
            />
            <MetricCard
              label={t('budgetPlanner.metrics.conversions')}
              value={fmt(selectedProjection.conversions, 1)}
              icon={Target}
              color="green"
              delta={calcDelta(selectedProjection.conversions, currentProjection?.conversions)}
              isCurrent={selectedProjection.is_current}
            />
            <MetricCard
              label={t('budgetPlanner.metrics.cost')}
              value={fmt(selectedProjection.cost, 2)}
              unit={curSym}
              icon={CurrencyDollar}
              color="orange"
              delta={calcDelta(selectedProjection.cost, currentProjection?.cost)}
              isCurrent={selectedProjection.is_current}
            />
            <MetricCard
              label={t('budgetPlanner.metrics.cpc')}
              value={fmt(selectedProjection.cpc, 2)}
              unit={curSym}
              icon={Lightning}
              color="yellow"
              delta={calcDelta(selectedProjection.cpc, currentProjection?.cpc)}
              isCurrent={selectedProjection.is_current}
            />
            <MetricCard
              label={t('budgetPlanner.metrics.ctr')}
              value={`${fmt(selectedProjection.ctr, 1)}%`}
              icon={TrendUp}
              color="brand"
              isCurrent={selectedProjection.is_current}
            />
            <MetricCard
              label={t('budgetPlanner.metrics.roas')}
              value={`${fmt(selectedProjection.roas, 1)}x`}
              icon={ChartBar}
              color={selectedProjection.roas >= 2 ? 'green' : selectedProjection.roas >= 1 ? 'yellow' : 'red'}
              delta={calcDelta(selectedProjection.roas, currentProjection?.roas)}
              isCurrent={selectedProjection.is_current}
            />
          </SimpleGrid>

          {/* Comparison table */}
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <EdgeScroll>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 600 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('budgetPlanner.table.budget')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.clicks')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.conversions')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.cost')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.cpc')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.roas')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('budgetPlanner.table.costPerConv')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {projections.map(p => (
                  <Table.Tr
                    key={p.multiplier}
                    style={p.is_current ? { backgroundColor: 'var(--mantine-color-brand-0)' } : undefined}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <Text size="sm" fw={p.is_current ? 600 : 400} ff="monospace">
                          {fmt(p.budget_daily, 2)} {t('budgetPlanner.units.zlPerDayShort', { currency: curSym })}
                        </Text>
                        {p.is_current && <Badge size="xs" variant="light" color="brand">{t('budgetPlanner.table.current')}</Badge>}
                        {!p.is_current && (
                          <Text size="xs" c={p.multiplier > 1 ? 'green.6' : 'red.6'}>
                            {p.multiplier > 1 ? '+' : ''}{Math.round((p.multiplier - 1) * 100)}%
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace">{fmt(p.clicks)}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace">{fmt(p.conversions, 1)}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace">{fmt(p.cost, 2)} {curSym}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace">{fmt(p.cpc, 2)} {curSym}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace" c={p.roas >= 2 ? 'green.7' : p.roas >= 1 ? undefined : 'red.6'}>{fmt(p.roas, 1)}x</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" ff="monospace">{fmt(p.cost_per_conversion, 2)} {curSym}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </EdgeScroll>
          </Paper>

          {/* Disclaimer */}
          <Text size="xs" c="dimmed" ta="center">
            {t('budgetPlanner.disclaimer')}
          </Text>
        </Stack>
      )}

      {!plannerLoading && !projections && !selectedCampaign && (
        <Paper withBorder p="xl" radius="md" ta="center">
          <ThemeIcon variant="light" color="brand" size="xl" radius="md" mx="auto" mb="sm">
            <ChartBar size={24} />
          </ThemeIcon>
          <Text size="sm" fw={500} mb={4}>{t('budgetPlanner.empty.title')}</Text>
          <Text size="xs" c="dimmed" maw={400} mx="auto">
            {t('budgetPlanner.empty.text')}
          </Text>
        </Paper>
      )}
    </Box>
  );
}
